'use client';

import { useState } from 'react';
import {
  Button,
  Popover,
  PopoverTrigger,
  PopoverContent,
  CARGO_ICON_NAMES,
  resolveCargoIcon,
  cn,
} from '@rr/ui';

interface IconPickerProps {
  value: string | null;
  onChange: (icon: string | null) => void;
  disabled?: boolean;
}

/**
 * Curated lucide icon picker for goods. Stores the icon name string; the
 * name→component map is the single shared source in `@rr/ui` (cargo-icons),
 * consumed by both rr-admin and tenant-web.
 */
export function IconPicker({ value, onChange, disabled }: IconPickerProps) {
  const [open, setOpen] = useState(false);
  const Selected = resolveCargoIcon(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled} className="h-9 w-full justify-start gap-2">
          <Selected className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm">{value ?? 'Pick an icon'}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2">
        <div className="grid grid-cols-6 gap-1">
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
              className="col-span-6 mb-1 rounded-md px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted"
            >
              Clear icon
            </button>
          )}
          {CARGO_ICON_NAMES.map((name) => {
            const Icon = resolveCargoIcon(name);
            return (
              <button
                key={name}
                type="button"
                title={name}
                onClick={() => {
                  onChange(name);
                  setOpen(false);
                }}
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-md hover:bg-muted',
                  value === name && 'bg-primary/10 ring-1 ring-primary',
                )}
              >
                <Icon className="h-4 w-4" />
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
